/**
 * StoragePage — flat list of a project's stores (databases / data stores).
 *
 * Mounted at /projects/:projectId/stores as a child of ProjectLayout (so
 * the sidebar / header / project context come from the layout). Just the
 * list content lives here.
 *
 * Data: GET /datasets/stores → { ok, stores: [{ storeId, name, type,
 * description, schema, status, createdAt }] }. The list is scoped to the
 * caller's project server-side, so no query params are needed.
 *
 * Deliberately a FLAT list — NO detail page and NO create form. Stores are
 * created conversationally (ask the agent / Copilot), so the empty state
 * points the user at chat rather than offering a "+ New" button.
 *
 * Visual language is a deliberate clone of AppsPage / PipelinesPage:
 *   - same `pipelines-page` wrapper + `pipelines-header*` chrome
 *   - same shared DataTable atom with column class names from the
 *     pipelines stylesheet
 *   - same status-pill colour classes (`pipelines-status-*`), so no new
 *     colour palette is introduced.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DataTable from '../../components/shared/DataTable/DataTable';
import { Stores } from '../../services/stores';
import useProjects from '../../hooks/useProjects';
// Reuse the pipelines stylesheet for header / wrapper / column widths +
// status-pill colours so this list page looks like a sibling of Apps/Agents.
import '../PipelinesPage/PipelinesPage.css';
import './StoragePage.css';

// Map a store's raw `status` onto the shared pipelines status-pill colour
// keys so we reuse the existing palette (no new colours):
//   ready / active / available → green ("Ready")
//   provisioning / creating / pending → amber ("Provisioning")
//   error / failed → red ("Error")
//   anything else / null → gray ("Unknown")
const STATUS_KEY = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'ready' || s === 'active' || s === 'available' || s === 'running') return 'live';
  if (s === 'provisioning' || s === 'creating' || s === 'pending' || s === 'building') return 'building';
  if (s === 'error' || s === 'failed') return 'failed';
  return 'sleeping';
};
const STATUS_LABEL = {
  live: 'Ready',
  building: 'Provisioning',
  failed: 'Error',
  sleeping: 'Unknown',
};

const StoreStatus = ({ status }) => {
  const key = STATUS_KEY(status);
  // If we got a real status string but it didn't map to a known key, show
  // the raw value (title-cased-ish) rather than a misleading "Unknown".
  const label =
    key === 'sleeping' && status ? String(status) : STATUS_LABEL[key];
  return (
    <span className={`pipelines-status pipelines-status-${key}`}>
      <span className="pipelines-status-dot" />
      {label}
    </span>
  );
};

// storeId shown small + copyable. Click the chip to copy; a brief "Copied"
// confirmation replaces the id. Clicking does NOT bubble (rows have no
// onClick here, but keep it defensive in case that ever changes).
const StoreIdChip = ({ storeId }) => {
  const [copied, setCopied] = useState(false);
  if (!storeId) return <span className="store-id store-id--empty">—</span>;
  const copy = (e) => {
    e.stopPropagation();
    try {
      navigator.clipboard?.writeText(storeId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable — non-fatal */
    }
  };
  return (
    <button
      type="button"
      className="store-id"
      onClick={copy}
      title={`Click to copy: ${storeId}`}
    >
      <code className="store-id-text">{copied ? 'Copied!' : storeId}</code>
    </button>
  );
};

const StoragePage = () => {
  const { projectId } = useParams();

  // The /datasets registry authenticates with the PROJECT token (it rejects
  // the user session token), so resolve the current project's apiToken from
  // the cached projects list and pass it to Stores.list().
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const projectToken = projects?.find((p) => p.projectId === projectId)?.apiToken;

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Wait for the projects list before deciding — only surface a token error
    // once we know the project actually has no token (not just still loading).
    if (projectsLoading) return;
    if (!projectToken) {
      setLoading(false);
      setError('Could not resolve this project’s token — reload the page or reopen the project.');
      setStores([]);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    Stores.list(projectToken)
      .then((data) => {
        if (!alive) return;
        setStores(Array.isArray(data?.stores) ? data.stores : []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || 'Failed to load stores');
        setStores([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [projectId, projectToken, projectsLoading]);

  // Columns: NAME / TYPE (chip) / STATUS (colored dot) / DESCRIPTION
  // (truncated) / ID (small + copyable). Flat list — no row actions, no
  // detail navigation.
  const columns = [
    {
      header: 'NAME',
      key: 'name',
      className: 'pipelines-col-name',
      render: (s) => (
        <span className="pipelines-name-text">
          <span className="pipelines-name">{s.name || s.storeId || 'Untitled store'}</span>
          <StoreIdChip storeId={s.storeId} />
        </span>
      ),
    },
    {
      header: 'TYPE',
      key: 'type',
      render: (s) =>
        s.type ? <span className="store-type-chip">{s.type}</span> : <span className="store-muted">—</span>,
    },
    {
      header: 'STATUS',
      key: 'status',
      className: 'pipelines-col-status',
      render: (s) => <StoreStatus status={s.status} />,
    },
    {
      header: 'DESCRIPTION',
      key: 'description',
      render: (s) =>
        s.description ? (
          <span className="store-description" title={s.description}>
            {s.description}
          </span>
        ) : (
          <span className="store-muted">—</span>
        ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Storage &amp; Database · Zibby</title>
      </Helmet>

      <div className="pipelines-page">
        {(loading || stores.length > 0 || error) && (
          <div className="pipelines-header">
            <div className="pipelines-header-row">
              <h1 className="pipelines-title">Storage &amp; Database</h1>
            </div>
            <p className="pipelines-subtitle">
              Databases and data stores in this project. Stores are created
              conversationally — ask your agent or the Copilot to provision one.
            </p>
          </div>
        )}

        {error && !loading ? (
          <div className="store-error" role="alert">
            Could not load stores: {error}
          </div>
        ) : (
          <DataTable
            data={stores}
            columns={columns}
            loading={loading}
            rowKey="storeId"
            emptyState={{
              title: 'No stores yet',
              description:
                'No stores yet — ask your agent or the Copilot to create one.',
            }}
          />
        )}
      </div>
    </>
  );
};

export default StoragePage;
